import { useEffect, useState } from "react";
import {
  FaCoffee,
  FaCog,
  FaDownload,
  FaEye,
  FaInfoCircle,
  FaStop,
} from "react-icons/fa";
import useCupGrinderSensorLive from "../../../../Hooks/device/useCupGrinderSensorLive";
import {
  SectionCard,
  LiveBadge,
  Notice,
  CardGridSkeleton,
  Spinner,
  BTN_PRIMARY,
  BTN_SECONDARY,
  BTN_DANGER,
  SegmentedTabs,
} from "./MaintenanceUi";
import {
  CUP_GRINDER_HISTORY_LIMIT,
  cupPresenceText,
  downloadCupGrinderHistory,
  grinderPresenceText,
  hasValue,
  sensorTone,
  thresholdDistance,
} from "./cupGrinderSensorHistory";
import { getCupStatus } from "./deviceStatusDisplay";
import {
  buildSensitivityChange,
  sensitivityOf,
  thresholdOf,
} from "./cupGrinderSensitivity";
import SensorExplainer from "./SensorExplainer";

// Sozlesme: BayinerIOT-Workspace- docs/api-contracts/cup-grinder-sensor-live.md (#132)
// Fincan (göz başına) ve değirmen sensörlerinin ham farkı + hassasiyet (v1.1,
// 1-10; eşik hassasiyetten türer). Tüm gözler aynı anda izlenir; fincan ve
// değirmen AYRI kutularda ama TEK oturumdadır (iki kutunun butonu aynı
// oturumu başlatır/durdurur). Fincan kutusunda göz seçilir (özet şeridi tüm
// gözleri gösterir); her kutunun altında kendi açıklaması (SensorExplainer).

const REASON_TEXT = {
  forbidden: "Bu cihazı izleme yetkiniz yok.",
  not_found: "Cihaz bulunamadı.",
  invalid_serial: "Cihazın seri numarası geçersiz.",
  too_many: "Aynı anda en fazla 3 cihaz izlenebilir.",
  not_watching: "İzleme henüz hazır değil. Birkaç saniye sonra tekrar deneyin.",
  error: "Sunucu izlemeyi başlatamadı. Sayfayı yenileyip tekrar deneyin.",
};

const TONE_STYLE = {
  none: "bg-gray-50 border-gray-200 text-gray-400",
  edge: "bg-amber-50 border-amber-300 text-amber-800",
  present: "bg-emerald-50 border-emerald-300 text-emerald-800",
  absent: "bg-white border-gray-200 text-gray-800",
};

const signed = (n) => (n > 0 ? `+${n}` : String(n));

function SensorCard({ title, sensor, threshold, presenceText, brewing }) {
  const tone = sensorTone(sensor, threshold);
  const distance = thresholdDistance(sensor, threshold);
  const brewingLabel = Number.isFinite(brewing)
    ? getCupStatus(brewing).label
    : null;
  return (
    <div
      data-testid="sensor-card"
      data-tone={tone}
      className={`flex flex-col gap-1.5 rounded-lg border px-3 py-2.5 ${TONE_STYLE[tone]}`}
    >
      <div className="flex items-center justify-between gap-2">
        <span className="text-sm font-SemiBold">{title}</span>
        {tone === "edge" && (
          <span className="text-xs font-SemiBold">Sınırda</span>
        )}
      </div>
      <div className="flex items-baseline justify-between gap-2">
        <span className="text-2xl font-Bold tabular-nums">
          {hasValue(sensor) ? sensor.diff : "—"}
        </span>
        <span className="text-sm font-SemiBold">{presenceText(sensor)}</span>
      </div>
      <div className="text-xs opacity-80">
        {distance === null
          ? "Karttan veri yok"
          : `Eşiğe uzaklık: ${signed(distance)} (eşik ${threshold})`}
      </div>
      {brewingLabel && (
        <div className="text-xs opacity-80">Göz durumu: {brewingLabel}</div>
      )}
      {sensor && (
        <div className="text-xs opacity-80">
          Hassasiyet: {sensitivityOf(sensor)} (aşağıdaki tablodan değiştirilir)
        </div>
      )}
    </div>
  );
}

const STRIP_STYLE = {
  none: "border-gray-200 bg-gray-50 text-gray-400",
  edge: "border-amber-300 bg-amber-50 text-amber-800",
  present: "border-emerald-300 bg-emerald-50 text-emerald-800",
  absent: "border-gray-200 bg-white text-gray-700",
};

/** Tüm gözlerin küçük özeti — tıklanan göz seçilir. */
function CupStrip({ cups, cupThreshold, selected, onSelect }) {
  return (
    <div data-testid="cup-strip" className="flex flex-wrap gap-1.5">
      {cups.map((c) => {
        const tone = sensorTone(c, thresholdOf("cup", c, cupThreshold));
        const active = c.cup === selected;
        return (
          <button
            key={c.cup}
            type="button"
            data-testid="cup-strip-item"
            data-tone={tone}
            aria-label={`Göz ${c.cup} özeti`}
            aria-pressed={active}
            onClick={() => onSelect(c.cup)}
            className={`flex min-w-[4.5rem] flex-col items-start rounded-md border px-2 py-1 text-left transition-shadow ${
              STRIP_STYLE[tone]
            } ${active ? "ring-2 ring-main" : "hover:shadow-sm"}`}
          >
            <span className="text-[10px] font-SemiBold">Göz {c.cup}</span>
            <span className="text-sm font-Bold tabular-nums">
              {hasValue(c) ? c.diff : "—"}
            </span>
          </button>
        );
      })}
    </div>
  );
}

/** Ortak oturum butonu (fincan + değirmen birlikte izlenir). */
function WatchControls({ running, pending, disabled, onStart, onStop }) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      <p className="text-xs text-gray-500 min-w-0 flex-1">
        Fincan ve değirmen birlikte izlenir; birini durdurmak ikisini de
        durdurur.
      </p>
      {running ? (
        <button type="button" onClick={onStop} className={BTN_DANGER}>
          <FaStop className="w-3 h-3" />
          İzlemeyi durdur
        </button>
      ) : (
        <button
          type="button"
          onClick={onStart}
          disabled={disabled || pending}
          className={BTN_PRIMARY}
        >
          {pending ? <Spinner /> : <FaEye className="w-3.5 h-3.5" />}
          Sensör izlemeyi başlat
        </button>
      )}
    </div>
  );
}

/**
 * Bakım sekmesi — fincan ve değirmen sensörleri canlı izleme
 * (yalnız Türk kahvesi gözü olan cihazda render edilir). İki kutu döner
 * (fincan + değirmen); yerleşimi MaintenanceTab'daki sensör satırı yapar.
 * Soket bağlantısı MaintenanceTab'dan gelir (sekmenin tek bağlantısı).
 * @param {object} props
 * @param {boolean} [props.hasGrinder] cihaz bilgisinde değirmen var mı —
 *   canlı yük gelince makinenin cevabı (grinder null/obje) geçerlidir
 * @param {string} [props.boxClassName] her kutunun dış sınıfı
 */
export default function CupGrinderSensorSection({
  socket,
  ready,
  serialNo,
  cupCount,
  onWriteSetting,
  hasGrinder = true,
  boxClassName = "",
}) {
  const {
    status,
    error,
    live,
    closedReason,
    running,
    remoteError,
    history,
    start,
    stop,
  } = useCupGrinderSensorLive(socket, ready, serialNo);
  // Başlat gönderildi, server onayı bekleniyor (buton döner).
  const [pending, setPending] = useState(false);
  useEffect(() => {
    setPending(false);
  }, [running, remoteError]);
  const [selectedCup, setSelectedCup] = useState(1);

  // Hassasiyet yazımı (açıklama panelindeki 1-10 kutularından).
  const saveSensitivity = (kind, cup) =>
    onWriteSetting
      ? (value) =>
          onWriteSetting(
            buildSensitivityChange(kind, cup, value),
            kind === "grinder"
              ? "Değirmen sensörü"
              : `Göz ${cup} fincan sensörü`,
          )
      : undefined;

  const badge = live ? "live" : running ? "waiting" : "off";
  const cups = Array.isArray(live?.cups) ? live.cups : [];
  const watchError = status === "error" ? error : null;
  // Makine cevap verdiyse değirmen kararı yükten (null = değirmen yok).
  const showGrinder = live ? !!live.grinder : hasGrinder;

  const cupNumbers = cups.length
    ? cups.map((c) => c.cup)
    : Array.from({ length: cupCount || 1 }, (_, i) => i + 1);
  const cupNo = cupNumbers.includes(selectedCup) ? selectedCup : cupNumbers[0];
  const selected = cups.find((c) => c.cup === cupNo) || null;

  const controls = (
    <>
      <WatchControls
        running={running}
        pending={pending}
        disabled={status !== "watching"}
        onStart={() => {
          setPending(true);
          start();
        }}
        onStop={stop}
      />
      {(watchError || remoteError) && (
        <Notice icon={FaInfoCircle} tone="rose" title="İzleme başlatılamadı">
          {REASON_TEXT[watchError || remoteError] || watchError || remoteError}
        </Notice>
      )}
    </>
  );

  // Canlı değer yokken (bekleniyor / kapalı) kutu içi durum.
  const idle = (count) =>
    running ? (
      <div className="flex flex-col gap-2">
        <div className="flex items-center gap-2 text-xs text-gray-500">
          <Spinner className="w-3 h-3" />
          Sensörler ölçülüyor… Birkaç saniye içinde gelmezse makinede Operasyon
          Modu kapalı olabilir ya da makine çevrimdışıdır.
        </div>
        <CardGridSkeleton count={count} />
      </div>
    ) : (
      <Notice icon={FaInfoCircle} tone="gray" title="Sensörler izlenmiyor">
        {closedReason === "session_limit"
          ? "Yayın 10 dakika sınırına ulaştığı için durdu. Yeniden başlatabilirsiniz."
          : "Sensör izlemeyi başlat'a basın. Makinede Operasyon Modu açık olmalı."}
      </Notice>
    );

  return (
    <>
      <SectionCard
        testId="cup-sensor-box"
        className={boxClassName}
        icon={FaCoffee}
        iconBg="bg-sixth"
        title="FİNCAN SENSÖRÜ"
        right={
          <>
            {history.length > 0 && (
              <button
                type="button"
                onClick={() => downloadCupGrinderHistory(history, serialNo)}
                title={`Son ${CUP_GRINDER_HISTORY_LIMIT} okumaya kadar (göz ve değirmen farkı, durum) CSV olarak indirilir`}
                className={BTN_SECONDARY}
              >
                <FaDownload className="w-3.5 h-3.5" />
                Son {history.length} değeri indir
              </button>
            )}
            <LiveBadge state={badge} />
          </>
        }
      >
        {controls}
        <div className="flex flex-wrap items-center gap-3">
          <span className="text-xs font-SemiBold tracking-wide text-gray-500">
            GÖZ
          </span>
          <SegmentedTabs
            options={cupNumbers.map((n) => ({ id: n, label: String(n) }))}
            value={cupNo}
            onChange={setSelectedCup}
          />
        </div>
        {!live ? (
          idle(1)
        ) : cups.length === 0 ? (
          <p className="text-xs text-gray-500">Makine göz verisi göndermedi.</p>
        ) : (
          <>
            <CupStrip
              cups={cups}
              cupThreshold={live.cupThreshold}
              selected={cupNo}
              onSelect={setSelectedCup}
            />
            <SensorCard
              title={`Göz ${cupNo}`}
              sensor={selected}
              threshold={thresholdOf("cup", selected, live.cupThreshold)}
              presenceText={cupPresenceText}
              brewing={selected?.brewing}
            ></SensorCard>
          </>
        )}
        <SensorExplainer
          key={`cup-${cupNo}`}
          live={live}
          history={history}
          sensorId={`cup-${cupNo}`}
          onSaveSensitivity={saveSensitivity("cup", cupNo)}
        />
      </SectionCard>

      {showGrinder && (
        <SectionCard
          testId="grinder-sensor-box"
          className={boxClassName}
          icon={FaCog}
          iconBg="bg-mainSecond"
          title="DEĞİRMEN SENSÖRÜ"
          right={<LiveBadge state={badge} />}
        >
          {controls}
          {!live ? (
            idle(1)
          ) : (
            <SensorCard
              title="Değirmen"
              sensor={live.grinder}
              threshold={thresholdOf(
                "grinder",
                live.grinder,
                live.grinderThreshold,
              )}
              presenceText={grinderPresenceText}
            />
          )}
          <SensorExplainer
            live={live}
            history={history}
            sensorId="grinder"
            onSaveSensitivity={saveSensitivity("grinder", null)}
          />
        </SectionCard>
      )}
    </>
  );
}
